/* ============================================================================
 * bundle-picker.js
 *
 * Native replacement for the MBC Bundles modal flow on the product page.
 * Wires the Bundle & Save tier selector, opens a self-contained modal with
 * N rows for picking colour + size, and submits all line items to
 * /cart/add.js in a single request. Discounting is handled by Shopify
 * automatic discounts at the cart, not here — we just collect variants.
 * ========================================================================== */
(function () {
  'use strict';

  // Bundle tier → how many of the N items the customer actually pays for.
  // "Buy 3 + 1 Free" is tier 4 paying for 3; "Buy 5 + 2 Free" is tier 7 paying
  // for 5. The tier selector in main-product.liquid also emits these as
  // data-packamount / data-packpaid and those win over this table — it is only
  // the fallback for a tier the markup didn't annotate. Display only; Shopify
  // applies the real discount at the cart.
  const TIER_PAID = { 1: 1, 4: 3, 7: 5 };

  // Units actually paid for at a given tier. Unknown tier → no discount.
  function paidUnits(ctx, tier) {
    const fromMarkup = ctx && ctx.tierPaid && ctx.tierPaid[tier];
    return fromMarkup || TIER_PAID[tier] || tier;
  }

  // Which rows are the free ones. The offer is presented as whole free items
  // rather than a discount shaved off every row — the LAST (tier - paid) rows
  // are free — and that is also what Shopify's "buy X get Y" automatic discount
  // does at the cart: it zeroes Y whole lines. Every row here is the same
  // garment at the same price, so which rows get picked is presentation only
  // and the total agrees either way.
  function isFreeRow(ctx, index) {
    if (packPrice(ctx, ctx.currentTier) != null) return false;
    return index >= paidUnits(ctx, ctx.currentTier);
  }

  // Fixed-price packs ("2 for $99", the strapless deal) carry the pack's total
  // in cents as data-packprice on the tier label. Such a tier has no free rows:
  // every row shows its own price and the footer total is the pack price.
  // Returns null for an ordinary buy-X-get-Y tier.
  function packPrice(ctx, tier) {
    const cents = ctx && ctx.tierPrice && ctx.tierPrice[tier];
    return cents > 0 ? cents : null;
  }

  // What a row costs inside a fixed-price pack, mirroring how checkout prices
  // it. The pack discount is a Shopify "Buy X get Y" (buy 1, get 1 $X off), so
  // checkout charges every bra its normal price except ONE, which carries the
  // whole discount: $69 + $41 = $110, $59 + $40 = $99. The last row plays that
  // discounted bra and is priced as the pack total minus the other rows.
  // Null = "show this row at its own price" (every other row, or any row of a
  // tier that is not fixed-price).
  function fixedRowShare(ctx, index) {
    const total = packPrice(ctx, ctx.currentTier);
    if (total == null) return null;
    const n = ctx.currentTier;
    if (index !== n - 1) return null;
    let others = 0;
    for (let i = 0; i < n - 1; i++) {
      const r = ctx.rows && ctx.rows[i];
      const v = r && (r.variant || r.previewVariant);
      others += v ? Number(v.price) || 0 : Number(ctx.product && ctx.product.price) || 0;
    }
    return Math.max(0, total - others);
  }

  // Colour normalisation, byte-identical to assets/complete-the-set.js's
  // _handleize (and global.js's colour matching) so a ghost cutout filename and
  // a swatch value can never disagree about what "STRAWBERRIES & CREAM"
  // handleizes to.
  function handleizeColour(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/&amp;/g, '-')
      .replace(/-and-/g, '-')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }

  function optionIndex(product, candidates) {
    if (!product || !Array.isArray(product.options)) return -1;
    for (let i = 0; i < product.options.length; i++) {
      const name = String(product.options[i] || '').toLowerCase().trim();
      if (candidates.indexOf(name) !== -1) return i;
    }
    return -1;
  }

  // Read a currently-checked radio value from the main product form by its
  // `name` (matches markup at main-product.liquid lines ~1097 and ~1218 where
  // each option renders a radio group named exactly after the option, e.g.
  // "Color"/"Colour"/"Size"). Falls back to a case-insensitive sweep so we
  // don't care whether the merchant spells it "Color" or "Colour".
  function readMainProductOption(optionName) {
    if (!optionName) return null;
    const exact = document.querySelector(
      'input[type="radio"][name="' + optionName + '"]:checked'
    );
    if (exact && exact.value) return exact.value;
    const all = document.querySelectorAll('input[type="radio"]:checked');
    for (let i = 0; i < all.length; i++) {
      const input = all[i];
      if (
        input.name &&
        input.name.toLowerCase() === optionName.toLowerCase() &&
        input.value
      ) {
        return input.value;
      }
    }
    return null;
  }

  /* ---------- Tier selector wiring -------------------------------------- */

  function syncCurrentTierFromActive(ctx) {
    const active = ctx.wrapper.querySelector('.bundle-selection .active-label');
    if (!active) {
      ctx.currentTier = 1;
      return;
    }
    const label = active.closest('label');
    if (!label) return;
    if (label.hasAttribute('data-reset')) {
      ctx.currentTier = 1;
    } else {
      const n = parseInt(label.getAttribute('data-packamount'), 10);
      ctx.currentTier = isNaN(n) ? 1 : n;
    }
  }

  // Build tier → paid-units from the markup so the Liquid stays the single
  // source of truth for the offer (e.g. data-packamount="4" data-packpaid="3").
  function readTierPaidFromMarkup(ctx) {
    const map = {};
    const prices = {};
    ctx.tierLabels.forEach(function (label) {
      const n = parseInt(label.getAttribute('data-packamount'), 10);
      const paid = parseInt(label.getAttribute('data-packpaid'), 10);
      const price = parseInt(label.getAttribute('data-packprice'), 10);
      if (!isNaN(n) && !isNaN(paid) && paid > 0) map[n] = paid;
      if (!isNaN(n) && !isNaN(price) && price > 0) prices[n] = price;
    });
    ctx.tierPaid = map;
    ctx.tierPrice = prices;
  }

  function wireTierSelector(ctx) {
    ctx.tierLabels.forEach(function (label) {
      label.addEventListener('click', function () {
        // Update visual active state — match prior product-bundle.js behaviour.
        ctx.wrapper.querySelectorAll('.active-label').forEach(function (el) {
          el.classList.remove('active-label');
        });
        const dot = label.querySelector('.custom-radio-dot');
        if (dot) dot.classList.add('active-label');

        // Update tier state.
        if (label.hasAttribute('data-reset')) {
          ctx.currentTier = 1;
        } else {
          const n = parseInt(label.getAttribute('data-packamount'), 10);
          ctx.currentTier = isNaN(n) ? 1 : n;
        }

        updateCtaText(ctx, label);
        updateAtcVisibility(ctx);
      });
    });
  }

  // When a multi-pack tier is active, the bundle CTA is the entry point and the
  // regular product-form ATC is hidden. When Single is active, the regular ATC
  // takes over (no modal, no bundle properties).
  function updateAtcVisibility(ctx) {
    const fakeBtns = document.querySelector('.fake-form-btns');
    const formBtns = document.querySelector('.product-form__buttons');
    const showBundle = ctx.currentTier > 1;
    if (fakeBtns) fakeBtns.style.display = showBundle ? 'block' : 'none';
    // The page-load <style> that hides the regular ATC on packed-default pages
    // would otherwise keep hiding it after Single hands back to the stylesheet.
    // From here on the inline display below owns the swap.
    const startHide = document.querySelector('style[data-bundle-start-hide]');
    if (startHide) startHide.remove();
    // Single hands visibility back to the stylesheet rather than forcing
    // 'block' — an inline display beats `.show-notify-me .product-form__buttons
    // { display: none }`, which left Add to Cart showing beside NOTIFY ME on a
    // sold-out variant.
    if (formBtns) formBtns.style.display = showBundle ? 'none' : '';
  }

  function updateCtaText(ctx, activeLabel) {
    if (!ctx.ctaBtn) return;
    const n = ctx.currentTier;
    const priceEl = activeLabel.querySelector('.pack-item-price-discounted');
    const priceText = priceEl ? priceEl.textContent.trim() : '';
    const noun = n === 1 ? 'item' : 'items';
    ctx.ctaBtn.innerHTML = 'ADD ' + n + ' ' + noun.toUpperCase() + ' TO CART &nbsp; ' + priceText;
  }

  /* ---------- Modal open / close ---------------------------------------- */

  function wireCta(ctx) {
    if (!ctx.ctaBtn) return;
    ctx.ctaBtn.addEventListener('click', function () {
      openModal(ctx, ctx.currentTier);
    });
    // Warm the thumbnail on intent as well as at idle. Idle covers the colour
    // the page loaded on; these cover a colour picked since, and on a slow
    // connection the gap between reaching for the button and pressing it is
    // worth a few hundred ms of head start. resolveThumb caches, so a warm
    // that has already happened costs nothing.
    ['pointerenter', 'focus', 'touchstart'].forEach(function (evt) {
      ctx.ctaBtn.addEventListener(evt, function () { warmThumbs(ctx); }, { passive: true });
    });
    // Allow keyboard activation since the CTA is a div.
    ctx.ctaBtn.setAttribute('role', 'button');
    ctx.ctaBtn.setAttribute('tabindex', '0');
    ctx.ctaBtn.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ' || e.code === 'Space') {
        e.preventDefault();
        openModal(ctx, ctx.currentTier);
      }
    });
  }

  function wireModalDismiss(ctx) {
    ctx.modal.querySelectorAll('[data-bundle-close]').forEach(function (el) {
      el.addEventListener('click', function () {
        closeModal(ctx);
      });
    });
  }

  function openModal(ctx, n) {
    if (!ctx.product || !Array.isArray(ctx.product.variants) || ctx.product.variants.length === 0) {
      console.warn('[bundle-picker] product has no variants');
      return;
    }
    ctx.lastTrigger = document.activeElement;

    // Reset state
    hideError(ctx);
    ctx.rows = [];
    ctx.rowsContainer.innerHTML = '';
    ctx.countEls.forEach(function (el) { el.textContent = String(n); });
    if (ctx.packnumEl) ctx.packnumEl.textContent = String(n);
    setSubmitLabel(ctx, n);

    // Sync from the main product form: pre-fill colour AND size for each row
    // so the modal opens with a fully-resolved variant. The customer can
    // change either per row before adding to cart.
    const mainSize = ctx.sizeIndex !== -1
      ? readMainProductOption(ctx.product.options[ctx.sizeIndex])
      : null;
    const mainColor = ctx.colorIndex !== -1
      ? readMainProductOption(ctx.product.options[ctx.colorIndex])
      : null;

    for (let i = 0; i < n; i++) {
      const row = createRow(ctx, i);
      ctx.rowsContainer.appendChild(row.el);
      ctx.rows.push(row);
      preselectRow(ctx, row, mainColor, mainSize);
    }
    refreshTotals(ctx);

    // Show
    ctx.modal.classList.remove('-closed');
    ctx.modal.removeAttribute('hidden');
    document.body.classList.add('stop-scroll');

    // Focus trap (uses theme's global utilities)
    if (typeof window.trapFocus === 'function') {
      const firstFocusable = ctx.modalContent.querySelector(
        'button, [href], select, input, [tabindex]:not([tabindex="-1"])'
      ) || ctx.modalContent;
      window.trapFocus(ctx.modalContent, firstFocusable);
    } else {
      ctx.modalContent.focus();
    }

    // Esc to close
    ctx.escHandler = function (e) {
      if (e.key === 'Escape' || e.code === 'Escape') {
        e.stopPropagation();
        closeModal(ctx);
      }
    };
    document.addEventListener('keydown', ctx.escHandler);
  }

  function closeModal(ctx) {
    ctx.modal.classList.add('-closed');
    ctx.modal.setAttribute('hidden', '');
    document.body.classList.remove('stop-scroll');
    if (ctx.escHandler) {
      document.removeEventListener('keydown', ctx.escHandler);
      ctx.escHandler = null;
    }
    if (typeof window.removeTrapFocus === 'function') {
      window.removeTrapFocus(ctx.lastTrigger || null);
    } else if (ctx.lastTrigger && typeof ctx.lastTrigger.focus === 'function') {
      ctx.lastTrigger.focus();
    }
  }

  /* ---------- Row rendering --------------------------------------------- */

  function createRow(ctx, index) {
    const product = ctx.product;
    const rowEl = document.createElement('div');
    rowEl.className = 'bundle-row';
    rowEl.setAttribute('data-row-index', String(index));

    // ---- thumbnail (defaults to product's featured image)
    const thumbEl = document.createElement('div');
    thumbEl.className = 'bundle-row__thumb';
    rowEl.appendChild(thumbEl);

    // ---- controls
    const controls = document.createElement('div');
    controls.className = 'bundle-row__controls';

    // Colour swatches (only if product has a colour option). Mirrors the main
    // product's exact DOM structure so the existing `.product-form__input
    // input[type=radio]+label` CSS applies — identical styling for free.
    let swatchesEl = null;
    if (ctx.colorIndex !== -1) {
      const groupName = 'bundle-color-' + index + '-' + Math.random().toString(36).slice(2, 8);

      const colorHeader = document.createElement('div');
      colorHeader.className = 'color-swatches-header';
      const colorLegend = document.createElement('span');
      colorLegend.className = 'form__label';
      colorLegend.textContent = product.options[ctx.colorIndex] + ':';
      colorHeader.appendChild(colorLegend);
      controls.appendChild(colorHeader);

      swatchesEl = document.createElement('div');
      swatchesEl.className = 'bundle-row__swatches product-form__input color-swatches';
      swatchesEl.setAttribute('role', 'radiogroup');
      swatchesEl.setAttribute(
        'aria-label',
        product.options[ctx.colorIndex] + ' for item ' + (index + 1)
      );

      const colorValues = uniqueOptionValues(product, ctx.colorIndex);
      colorValues.forEach(function (val, vIdx) {
        const swatchInfo = ctx.colorSwatches[val] || {};
        const valueHandle = swatchInfo.handle || val.toLowerCase().replace(/\s+/g, '-');
        const inputId = groupName + '-' + vIdx;

        const wrap = document.createElement('div');
        wrap.className = 'filter__checkbox filter__checkbox--color';

        const input = document.createElement('input');
        input.type = 'radio';
        input.id = inputId;
        input.name = groupName;
        input.value = val;
        input.setAttribute('data-color', val);
        input.addEventListener('change', function () {
          if (input.checked) selectColor(ctx, row, val);
        });
        wrap.appendChild(input);

        const lbl = document.createElement('label');
        lbl.setAttribute('for', inputId);
        lbl.setAttribute('data-swatch-color', valueHandle);
        lbl.setAttribute('aria-label', val);
        lbl.title = val;
        if (swatchInfo.image) lbl.style.backgroundImage = 'url(' + swatchInfo.image + ')';
        if (swatchInfo.hex) lbl.style.backgroundColor = swatchInfo.hex;
        // Match the main product's special-cased white/light swatch class so
        // it gets a visible border (otherwise white-on-white disappears).
        if (
          valueHandle === 'white' ||
          valueHandle === 'snow-cone' ||
          valueHandle === 'marshmallow'
        ) {
          lbl.classList.add('white-color');
        }
        wrap.appendChild(lbl);

        const tip = document.createElement('div');
        tip.className = 'tooltip';
        tip.textContent = val;
        wrap.appendChild(tip);

        swatchesEl.appendChild(wrap);
      });
      controls.appendChild(swatchesEl);
    }

    // Size pills (only if product has a size option). Every size the product
    // sells is always rendered — one out of stock in the chosen colour is
    // disabled rather than dropped, so the row does not reflow as colours
    // change and the shopper can see what the colour is missing.
    let sizesEl = null;
    if (ctx.sizeIndex !== -1) {
      const sizeLabel = document.createElement('span');
      sizeLabel.className = 'bundle-row__group-label';
      sizeLabel.textContent = product.options[ctx.sizeIndex] + ':';
      controls.appendChild(sizeLabel);

      sizesEl = document.createElement('div');
      sizesEl.className = 'bundle-row__sizes';
      sizesEl.setAttribute('role', 'radiogroup');
      sizesEl.setAttribute(
        'aria-label',
        product.options[ctx.sizeIndex] + ' for item ' + (index + 1)
      );
      controls.appendChild(sizesEl);
    }

    rowEl.appendChild(controls);

    // ---- price column
    const priceEl = document.createElement('div');
    priceEl.className = 'bundle-row__price';
    const priceEmpty = document.createElement('span');
    priceEmpty.className = 'bundle-row__price-empty';
    priceEmpty.textContent = '—';
    priceEl.appendChild(priceEmpty);
    rowEl.appendChild(priceEl);

    const row = {
      index: index,
      el: rowEl,
      thumbEl: thumbEl,
      thumbKey: null,
      swatchesEl: swatchesEl,
      sizesEl: sizesEl,
      priceEl: priceEl,
      color: null,
      size: null,
      variant: null,
      previewVariant: null,
    };

    // Default thumbnail: the product's own ghost cutout, no colour yet.
    renderRowThumb(ctx, row, null);

    // Render size pills now; they are re-rendered with per-colour stock once a
    // colour is picked.
    if (sizesEl) renderSizePills(ctx, row);

    // If the product has neither colour nor size, just pick the first available variant.
    if (ctx.colorIndex === -1 && ctx.sizeIndex === -1) {
      const v = product.variants.find(function (vv) { return vv.available; }) || product.variants[0];
      row.variant = v;
      renderRowPrice(ctx, row, v);
      renderRowThumb(ctx, row, v);
    } else {
      // Show a sample price upfront — picked variants will replace it as the
      // user makes selections.
      row.previewVariant = findRepresentativeVariant(ctx, row);
      if (row.previewVariant) renderRowPrice(ctx, row, row.previewVariant);
    }

    return row;
  }

  function uniqueOptionValues(product, optionIndex) {
    const seen = new Set();
    const out = [];
    product.variants.forEach(function (v) {
      const val = v['option' + (optionIndex + 1)];
      if (val && !seen.has(val)) {
        seen.add(val);
        out.push(val);
      }
    });
    return out;
  }

  // Apply default colour/size to a freshly-created row. Colour is set first so
  // the size dropdown's stock filter is correct before we try to honour size.
  function preselectRow(ctx, row, color, size) {
    if (color && row.swatchesEl) {
      const input = row.swatchesEl.querySelector(
        'input[type="radio"][data-color="' + cssEscape(color) + '"]'
      );
      if (input) selectColor(ctx, row, color);
    }
    if (size && row.sizesEl) {
      if (findSizePill(row, size)) {
        row.size = size;
        markSizePillActive(row);
        resolveVariant(ctx, row);
      }
    }
  }

  function selectColor(ctx, row, color) {
    row.color = color;
    if (row.swatchesEl) {
      row.swatchesEl.querySelectorAll('input[type="radio"]').forEach(function (input) {
        input.checked = input.getAttribute('data-color') === color;
      });
    }
    if (row.sizesEl) {
      // Stock is per colour+size, so the pills have to be rebuilt. Keep the
      // shopper's size if the new colour still carries it, otherwise clear it.
      const previousSize = row.size;
      row.size = null;
      renderSizePills(ctx, row);
      if (previousSize && findSizePill(row, previousSize)) {
        row.size = previousSize;
        markSizePillActive(row);
      }
    }
    resolveVariant(ctx, row);
  }

  function selectSize(ctx, row, size) {
    row.size = size || null;
    markSizePillActive(row);
    resolveVariant(ctx, row);
  }

  // Selectable pill (or dropdown option) for `size`, or null when the size is
  // absent or disabled.
  function findSizePill(row, size) {
    if (!row.sizesEl || !size) return null;
    const sel = '[data-size="' + cssEscape(size) + '"]:not([disabled])';
    return row.sizesEl.querySelector('.bundle-size-pill' + sel + ', option' + sel);
  }

  function markSizePillActive(row) {
    if (!row.sizesEl) return;
    const select = row.sizesEl.querySelector('select');
    if (select) {
      select.value = row.size || '';
      return;
    }
    row.sizesEl.querySelectorAll('.bundle-size-pill').forEach(function (pill) {
      const on = pill.getAttribute('data-size') === row.size;
      pill.classList.toggle('is-selected', on);
      pill.setAttribute('aria-checked', on ? 'true' : 'false');
    });
  }

  function renderSizePills(ctx, row) {
    const host = row.sizesEl;
    if (!host) return;
    const product = ctx.product;
    const sizeIdx = ctx.sizeIndex;
    const colorIdx = ctx.colorIndex;

    host.innerHTML = '';
    const sizes = uniqueOptionValues(product, sizeIdx);

    // Dropdown mode (bras — the strapless 2-pack): a bra size run is far too
    // long for a row of pills, so it gets the same "Select a size" dropdown as
    // the cart upsell cards. Sold-out sizes stay listed but disabled.
    if (ctx.sizeUi === 'select') {
      const select = document.createElement('select');
      select.className = 'bundle-row__size-select';
      select.setAttribute('aria-label', product.options[sizeIdx] + ' for item ' + (row.index + 1));
      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.disabled = true;
      placeholder.selected = true;
      placeholder.textContent = 'Select a size';
      select.appendChild(placeholder);
      sizes.forEach(function (size) {
        const available = product.variants.some(function (v) {
          if (colorIdx !== -1 && row.color && v['option' + (colorIdx + 1)] !== row.color) return false;
          return v['option' + (sizeIdx + 1)] === size && v.available;
        });
        const opt = document.createElement('option');
        opt.value = size;
        opt.setAttribute('data-size', size);
        opt.textContent = available ? size : size + ' — Sold out';
        if (!available) opt.disabled = true;
        select.appendChild(opt);
      });
      select.addEventListener('change', function () { selectSize(ctx, row, select.value); });
      host.appendChild(select);
      host.classList.add('bundle-row__sizes--select');
      markSizePillActive(row);
      return;
    }

    sizes.forEach(function (size) {
      const matchingVariants = product.variants.filter(function (v) {
        if (colorIdx !== -1 && row.color && v['option' + (colorIdx + 1)] !== row.color) return false;
        return v['option' + (sizeIdx + 1)] === size;
      });
      // If the product has additional options we don't know about, treat the
      // size as available when any matching variant is.
      const anyAvailable = matchingVariants.some(function (v) { return v.available; });
      const exists = matchingVariants.length > 0;

      const pill = document.createElement('button');
      pill.type = 'button';
      pill.className = 'bundle-size-pill';
      pill.setAttribute('role', 'radio');
      pill.setAttribute('aria-checked', 'false');
      pill.setAttribute('data-size', size);
      pill.textContent = size;

      if (!exists || !anyAvailable) {
        pill.disabled = true;
        pill.classList.add('is-unavailable');
        // The strikethrough is the visual cue; spell it out for a screen reader.
        pill.setAttribute(
          'aria-label',
          size + (exists ? ' — out of stock' : ' — unavailable')
        );
      } else {
        pill.addEventListener('click', function () { selectSize(ctx, row, size); });
      }
      host.appendChild(pill);
    });

    // The pill grid is one line on desktop and at most two on mobile, and CSS
    // cannot count children — hand it the counts it needs for its column
    // tracks. Up to three pills still fit one mobile line, so only split the
    // row in half beyond that. Out-of-stock sizes are still rendered, so this
    // count is the product's full size run and the grid holds its shape as the
    // shopper moves between colours.
    const count = sizes.length || 1;
    host.style.setProperty('--size-count', String(count));
    host.style.setProperty('--size-count-2up', String(count <= 3 ? count : Math.ceil(count / 2)));

    markSizePillActive(row);
  }

  function resolveVariant(ctx, row) {
    const product = ctx.product;
    const variant = product.variants.find(function (v) {
      if (ctx.colorIndex !== -1) {
        if (v['option' + (ctx.colorIndex + 1)] !== row.color) return false;
      }
      if (ctx.sizeIndex !== -1) {
        if (v['option' + (ctx.sizeIndex + 1)] !== row.size) return false;
      }
      return true;
    }) || null;

    row.variant = variant && variant.available ? variant : null;
    if (row.variant) {
      row.previewVariant = null;
      renderRowPrice(ctx, row, row.variant);
      renderRowThumb(ctx, row, row.variant);
    } else {
      // Selection is not yet complete or not available.
      const isComplete =
        (ctx.colorIndex === -1 || row.color) && (ctx.sizeIndex === -1 || row.size);
      if (variant && !variant.available && isComplete) {
        row.previewVariant = null;
        renderRowPriceUnavailable(row);
      } else {
        // Partial selection — show a sample price using a representative variant
        // of the chosen colour (most variants share a price). Falls back to the
        // first available variant of the product when no colour is picked yet.
        row.previewVariant = findRepresentativeVariant(ctx, row);
        if (row.previewVariant) {
          renderRowPrice(ctx, row, row.previewVariant);
        } else {
          renderRowPriceEmpty(ctx, row);
        }
      }
      // The ghost cutout only needs the colour plus the product that owns it,
      // so a colour with no size picked yet already shows the right image.
      // `variant` backstops the unavailable case, where previewVariant is
      // cleared but the resolved variant still knows which linked colour
      // product the cutout belongs to.
      renderRowThumb(ctx, row, row.previewVariant || variant);
    }
    refreshTotals(ctx);
  }

  // Find a stand-in variant for a row whose selection isn't complete yet.
  // Prefers an available variant matching the chosen colour, then any variant
  // of that colour, then any available variant of the product.
  function findRepresentativeVariant(ctx, row) {
    const variants = ctx.product.variants;
    if (!variants || !variants.length) return null;
    const colorIdx = ctx.colorIndex;
    const matchesColor = function (v) {
      if (colorIdx === -1 || !row.color) return true;
      return v['option' + (colorIdx + 1)] === row.color;
    };
    return (
      variants.find(function (v) { return matchesColor(v) && v.available; }) ||
      variants.find(matchesColor) ||
      variants.find(function (v) { return v.available; }) ||
      variants[0] ||
      null
    );
  }

  // Normal model shot for a row — the last step of the ghost chain below.
  function photoFor(ctx, variant) {
    const product = ctx.product || {};
    const src =
      (variant && variant.featured_image) ||
      product.featured_image ||
      (product.images && product.images[0]) ||
      null;
    if (!src) return null;
    return typeof src === 'string' ? src : src.src || null;
  }

  // Ghost cutout candidates, newest convention first. Mirrors the chain in
  // assets/complete-the-set.js (_applyImages):
  //   1. <product id>_<colour>.webp   colour-specific cutout
  //   2. <product id>.webp            the product's default cutout — the
  //                                   ORIGINAL convention, also used by
  //                                   snippets/ghost-image-map.liquid
  // The id is the product the variant really belongs to, which for a colour
  // supplied by `custom.product_color_variants` is the LINKED product.
  function ghostChain(ctx, variant, colour) {
    if (!ctx.ghostBase) return [];
    const pid = (variant && variant.product_id) || (ctx.product && ctx.product.id);
    if (!pid) return [];
    const chain = [];
    const handle = handleizeColour(colour);
    if (handle) chain.push(ctx.ghostBase + pid + '_' + handle + '.webp');
    chain.push(ctx.ghostBase + pid + '.webp');
    return chain;
  }

  // FREE stands in for the price on a free row whatever else is going on, so it
  // is checked before the price is even read. `data-free` on the row is what
  // the CSS keys off to tint the card.
  function renderRowFree(row) {
    row.el.setAttribute('data-free', '');
    row.priceEl.innerHTML =
      '<span class="bundle-row__price-free">FREE' +
      '<span class="bundle-row__price-free-icon" aria-hidden="true">\uD83C\uDF89</span>' +
      '</span>';
  }

  function renderRowPrice(ctx, row, variant) {
    if (isFreeRow(ctx, row.index)) {
      renderRowFree(row);
      return;
    }
    row.el.removeAttribute('data-free');

    let cents = Number(variant.price);
    let compareCents = Number(variant.compare_at_price);

    // Fixed-price pack: the discounted row (see fixedRowShare) shows what is
    // left of the pack price, with the garment price (or its RRP, if marked
    // down) struck through. The other rows show their normal price.
    const share = fixedRowShare(ctx, row.index);
    if (share != null && share < cents) {
      compareCents = Math.max(cents, compareCents || 0);
      cents = share;
    }

    row.priceEl.innerHTML = '';
    // Only a genuine markdown on the garment gets a strikethrough here. The
    // pack discount is expressed by the FREE rows, not by shaving every row,
    // so there is nothing of it left to strike through at row level.
    if (compareCents > cents) {
      const compare = document.createElement('span');
      compare.className = 'bundle-row__price-compare';
      compare.textContent = formatMoney(compareCents);
      row.priceEl.appendChild(compare);
    }
    const current = document.createElement('span');
    current.className = 'bundle-row__price-current';
    current.textContent = formatMoney(cents);
    row.priceEl.appendChild(current);
  }

  function renderRowPriceEmpty(ctx, row) {
    if (isFreeRow(ctx, row.index)) {
      renderRowFree(row);
      return;
    }
    row.priceEl.innerHTML = '<span class="bundle-row__price-empty">—</span>';
  }

  // Unavailable wins over FREE: a free row the shopper cannot actually have
  // still blocks the add, so saying so beats saying FREE.
  function renderRowPriceUnavailable(row) {
    row.el.removeAttribute('data-free');
    row.priceEl.innerHTML = '<span class="bundle-row__price-empty">Unavailable</span>';
  }

  /*
   * Thumbnail resolution, cached.
   *
   * A row's picture is found by letting an <img> 404 its way down the ghost
   * chain, so every miss costs a full round trip BEFORE the picture that does
   * exist is even requested. Most products have no colour-specific cutout, so
   * that was routinely one wasted round trip per open, on the critical path,
   * with the modal already on screen and the row empty.
   *
   * Resolving through this cache pays that walk at most once per colour per
   * page view. Reopening the modal, changing size, or flicking back to a
   * colour already seen then draws from memory. Combined with warmThumbs()
   * below — which runs the same resolution at idle, long before the modal is
   * opened — the common path becomes a straight cache hit.
   *
   * Values are { url, isGhost }; a null url means "nothing loadable", which is
   * cached too so a dead row is not re-probed on every render.
   */
  const thumbCache = new Map();

  // The thumb is drawn at 150px (CSS), 104px under 900px wide and 76px under
  // 360px. This used to request a flat width=400 for all of them — roughly 5x
  // the pixels a phone can show. Snapped to three widths rather than computed
  // exactly so shoppers share CDN derivatives instead of each minting a new one.
  function thumbWidth() {
    let css = 150;
    try {
      if (window.matchMedia('(max-width: 360px)').matches) css = 76;
      else if (window.matchMedia('(max-width: 900px)').matches) css = 104;
    } catch (_) {}
    const need = css * Math.min(window.devicePixelRatio || 1, 2);
    if (need <= 200) return 200;
    if (need <= 300) return 300;
    return 400;
  }

  function thumbCacheKey(chain, fallback) {
    return chain.join('|') + '::' + (fallback || '');
  }

  // Walk the chain off-screen and hand back the first URL that actually loads,
  // falling through to the model shot. Calls back synchronously on a cache hit.
  function resolveThumb(chain, fallback, cb) {
    const key = thumbCacheKey(chain, fallback);
    const hit = thumbCache.get(key);
    if (hit) { cb(hit.url, hit.isGhost); return; }

    const width = thumbWidth();
    const probe = new Image();
    const settle = function (url, isGhost) {
      probe.onload = null;
      probe.onerror = null;
      thumbCache.set(key, { url: url, isGhost: isGhost });
      cb(url, isGhost);
    };
    let step = -1;
    const advance = function () {
      step++;
      if (step < chain.length) {
        probe.src = chain[step] + '?width=' + width;
        return;
      }
      // Out of cutouts. This is the terminal step, so re-arm both handlers —
      // if the model shot 404s too there is nothing left to try, and
      // re-entering advance() would re-set the same src forever.
      if (!fallback) { settle(null, false); return; }
      probe.onload = function () { settle(fallback, false); };
      probe.onerror = function () { settle(null, false); };
      probe.src = fallback;
    };
    probe.onload = function () { settle(probe.src, true); };
    probe.onerror = advance;
    advance();
  }

  // Resolve (and so also prefetch) the thumbnail the modal will open on, before
  // it is opened. Every row opens on the SAME colour — the one selected on the
  // PDP — so this is one image for the whole pack, and by the time the modal
  // renders, both the chain outcome and the bytes are already in hand.
  function warmThumbs(ctx) {
    const product = ctx.product;
    if (!product || !Array.isArray(product.variants) || !product.variants.length) return;
    const color = ctx.colorIndex !== -1
      ? readMainProductOption(product.options[ctx.colorIndex])
      : null;
    const size = ctx.sizeIndex !== -1
      ? readMainProductOption(product.options[ctx.sizeIndex])
      : null;
    const variant = product.variants.find(function (v) {
      if (ctx.colorIndex !== -1 && v['option' + (ctx.colorIndex + 1)] !== color) return false;
      if (ctx.sizeIndex !== -1 && v['option' + (ctx.sizeIndex + 1)] !== size) return false;
      return true;
    }) || findRepresentativeVariant(ctx, { color: color, size: size });
    if (!variant) return;

    const noop = function () {};
    // createRow() draws a colourless thumb first and preselectRow() then
    // re-draws it with the shopper's colour, so an open resolves TWO keys.
    // Warm both or the first one still probes the network with the modal
    // already on screen. When the product has no colour-specific cutout these
    // collapse onto the same file anyway, so it is usually one fetch.
    resolveThumb(ghostChain(ctx, null, null), photoFor(ctx, null), noop);
    if (color) {
      resolveThumb(ghostChain(ctx, variant, color), photoFor(ctx, variant), noop);
    }
  }

  // Draw a row's thumbnail. `variant` may be null (nothing picked yet), which
  // just means no colour-specific step in the chain.
  function renderRowThumb(ctx, row, variant) {
    const chain = ghostChain(ctx, variant, row.color);
    const fallback = photoFor(ctx, variant);

    // The cutout is a function of colour, not of the exact variant — every size
    // of a colour resolves to the same chain. Picking a size would otherwise
    // tear down the <img> and start the load again, flashing the row for an
    // identical picture. Bail when nothing this function would draw has changed.
    const key = thumbCacheKey(chain, fallback);
    if (row.thumbKey === key && row.thumbEl.firstChild) return;
    row.thumbKey = key;

    row.thumbEl.innerHTML = '';
    // A cutout sits on the row's own background; a model shot owns its own and
    // must be cropped instead, which is what the CSS keys off. Assume a cutout
    // while resolving so the common case never repaints.
    row.thumbEl.setAttribute('data-state', chain.length ? 'ghost' : 'photo');

    const img = document.createElement('img');
    img.alt = '';
    img.decoding = 'async';
    // Deliberately NOT loading="lazy". Every row is on screen the instant the
    // modal opens, and a lazy image inside a container that was display:none
    // until this moment has to wait for a layout pass before the browser will
    // even queue it — the opposite of what this needs.
    img.setAttribute('fetchpriority', 'high');
    row.thumbEl.appendChild(img);

    resolveThumb(chain, fallback, function (url, isGhost) {
      // A colour change may have re-rendered this row while we resolved.
      if (row.thumbKey !== key) return;
      row.thumbEl.setAttribute('data-state', isGhost ? 'ghost' : 'photo');
      if (!url) { img.remove(); return; }
      img.src = url;
    });
  }

  /* ---------- Totals & submit gate -------------------------------------- */

  function refreshTotals(ctx) {
    // The discounted row's price depends on the OTHER rows' prices, so redraw
    // it whenever any row changes. Skipped while it shows "Unavailable"
    // (no variant and no preview).
    if (packPrice(ctx, ctx.currentTier) != null && ctx.rows.length) {
      const last = ctx.rows[ctx.rows.length - 1];
      const lastVariant = last.variant || last.previewVariant;
      if (lastVariant) renderRowPrice(ctx, last, lastVariant);
    }
    const completeRows = ctx.rows.filter(function (r) { return r.variant; });
    // Include preview variants so the total reflects an estimate even before
    // every row is fully picked. Submit stays gated on `completeRows` below.
    const priceOf = function (r) {
      const v = r.variant || r.previewVariant;
      return v ? Number(v.price) || 0 : 0;
    };
    const fullCents = ctx.rows.reduce(function (acc, r) {
      return acc + priceOf(r);
    }, 0);
    // The free rows are simply left out of the payable total. Same arithmetic
    // as the old pay-for-N ratio while every row is the same garment, and it
    // stays right if a linked colour is ever priced differently.
    const fixedCents = packPrice(ctx, ctx.currentTier);
    // A fixed pack is always exactly its price — $99 AU / $110 NZ — never
    // derived from the garment price.
    const discountedCents = fixedCents != null
      ? fixedCents
      : ctx.rows.reduce(function (acc, r) {
          return isFreeRow(ctx, r.index) ? acc : acc + priceOf(r);
        }, 0);

    if (discountedCents < fullCents && fullCents > 0) {
      ctx.compareEl.textContent = formatMoney(fullCents);
      ctx.compareEl.removeAttribute('hidden');
    } else {
      ctx.compareEl.textContent = '';
      ctx.compareEl.setAttribute('hidden', '');
    }

    if (fullCents > 0) {
      const suffix = ctx.currencyCode ? ' ' + ctx.currencyCode : '';
      ctx.currentEl.textContent = formatMoney(discountedCents) + suffix;
    } else {
      ctx.currentEl.textContent = '';
    }

    const allComplete = ctx.rows.length > 0 && completeRows.length === ctx.rows.length;
    setSubmitEnabled(ctx, allComplete);
  }

  function setSubmitEnabled(ctx, enabled) {
    if (enabled) {
      ctx.submitBtn.disabled = false;
      ctx.submitBtn.removeAttribute('aria-disabled');
    } else {
      ctx.submitBtn.disabled = true;
      ctx.submitBtn.setAttribute('aria-disabled', 'true');
    }
  }

  function setSubmitLabel(ctx /* n unused — copy stays "Add to Cart" */) {
    if (!ctx.submitLabel) return;
    ctx.submitLabel.textContent = 'Add to Cart';
  }

  /* ---------- Submit ---------------------------------------------------- */

  function tierLabelFor(n) {
    // Line item property tag for the cart, e.g. 4pack (Buy 3 + 1 Free),
    // 7pack (Buy 5 + 2 Free). Counts total items, not items paid for.
    return n + 'pack';
  }

  function getCartDrawer() {
    return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  }

  function buildSubmitPayload(ctx) {
    const tier = ctx.currentTier;
    const tierTag = tierLabelFor(tier);
    const items = ctx.rows.map(function (r) {
      const item = {
        id: r.variant.id,
        quantity: 1,
      };
      if (tier > 1) {
        item.properties = { _bundle_tier: tierTag };
      }
      return item;
    });
    const payload = { items: items };

    const cart = getCartDrawer();
    if (cart && typeof cart.getSectionsToRender === 'function') {
      payload.sections = cart.getSectionsToRender().map(function (s) { return s.id; });
      payload.sections_url = window.location.pathname;
    }
    return { payload: payload, cart: cart };
  }

  function submitBundle(ctx) {
    if (ctx.submitBtn.disabled) return;

    hideError(ctx);
    ctx.submitBtn.disabled = true;
    ctx.submitBtn.setAttribute('aria-disabled', 'true');
    ctx.submitBtn.classList.add('is-loading');

    const built = buildSubmitPayload(ctx);

    // Optimistic open: close picker, pop drawer with N populated
    // previews (one per bundle item) before the network call. Server
    // rendering 3–10 line items is the slowest path; this hides the
    // wait behind the existing drawer slide-in animation.
    closeModal(ctx);
    const optimisticQty = (built.payload.items || []).reduce(function (sum, it) {
      return sum + (parseInt(it.quantity, 10) || 1);
    }, 0);

    // Build preview data from the bundle rows. Each row holds the
    // resolved variant plus the bundle product context (title, options).
    // Zero the free rows optimistically so the preview shows the bundle's
    // discounted price + strikethrough original — matches how Shopify will
    // render it ~500ms later when the cart discount lands.
    //
    // Guard: if the cart already carries discounts (cart-level promo,
    // existing bundle line items, automatic discount) we can't safely
    // predict the final price — Shopify's combine rules may apply the
    // new tier differently. Skip the strikethrough and show regular
    // prices; the server-rendered line replaces this in ~500ms anyway.
    const cartHasDiscount = (function () {
      // 1) Live DOM check — most reliable, works regardless of lastCart
      //    freshness. Liquid renders <span class="cart-item__price-compare-at">
      //    strikethrough whenever a line item is discounted; existence of
      //    that span anywhere in the drawer means there's a line discount.
      const drawer = document.querySelector('cart-drawer');
      if (drawer && drawer.querySelector('.cart-item__price-compare-at')) return true;

      // 2) Inline state — #cart-drawer-state's total_discount field is
      //    populated from cart.total_discount on every section render,
      //    so this catches cart-level discounts even on the boot stub
      //    before the deferred /cart.js enrichment lands.
      const stateEl = document.getElementById('cart-drawer-state');
      if (stateEl) {
        try {
          const parsed = JSON.parse(stateEl.textContent);
          if (typeof parsed.total_discount === 'number' && parsed.total_discount > 0) return true;
        } catch (_) {}
      }

      // 3) lastCart — after the deferred /cart.js fetch lands we have
      //    the full cart object with line-level discount fields.
      const lc = window.cartEnhancements && window.cartEnhancements.lastCart;
      if (!lc) return false;
      if (typeof lc.total_discount === 'number' && lc.total_discount > 0) return true;
      if (Array.isArray(lc.cart_level_discount_applications) && lc.cart_level_discount_applications.length) return true;
      if (Array.isArray(lc.items)) {
        for (let i = 0; i < lc.items.length; i++) {
          const it = lc.items[i];
          if (it && Array.isArray(it.discount_allocations) && it.discount_allocations.length) return true;
          if (it && typeof it.original_price === 'number' && typeof it.final_price === 'number' && it.original_price > it.final_price) return true;
        }
      }
      return false;
    })();
    const fixedShare = packPrice(ctx, ctx.currentTier) != null
      ? function (index) { return fixedRowShare(ctx, index); }
      : null;
    const previewItems = (function () {
      try {
        const rows = ctx.rows || [];
        const productOptions = (ctx.product && Array.isArray(ctx.product.options))
          ? ctx.product.options
          : [];
        const out = [];
        rows.forEach(function (row) {
          const v = row.variant;
          if (!v) return;
          const image =
            (v.featured_image && v.featured_image.src) ||
            (v.featured_media && v.featured_media.preview_image && v.featured_media.preview_image.src) ||
            (ctx.product && ctx.product.featured_image) ||
            null;
          // Pair variant.options with product.options to get name+value
          // pairs the preview can render as "Size: X" + a colour swatch.
          let options = null;
          if (Array.isArray(v.options) && productOptions.length) {
            options = v.options.map(function (val, i) {
              // product.options entries may be strings (older shape) or
              // objects {name, position, values}. Handle both.
              const opt = productOptions[i];
              const name = (opt && typeof opt === 'object') ? opt.name : (typeof opt === 'string' ? opt : '');
              return { name: name, value: val };
            });
          }
          const rawPrice = typeof v.price === 'number' ? v.price : null;
          // Shopify's "buy X get Y" automatic discount zeroes whole lines
          // rather than shaving every line, so preview the free rows at 0 with
          // the original struck through. A cart that already carries discounts
          // is unpredictable, so it previews at full price (see
          // cartHasDiscount).
          const free = !cartHasDiscount && isFreeRow(ctx, row.index);
          let finalPrice = free ? 0 : rawPrice;
          // Fixed-price pack: price the lines the way checkout will — every
          // line at its normal price except the last, which carries the whole
          // pack discount ($69 + $41 = $110).
          const share = fixedShare ? fixedShare(row.index) : null;
          if (!cartHasDiscount && share != null && rawPrice != null && share < rawPrice) {
            finalPrice = share;
          }
          const qty = parseInt(row.quantity, 10) || 1;
          for (let i = 0; i < qty; i++) {
            out.push({
              title: (ctx.product && ctx.product.title) || v.name || '',
              image: image,
              variant_title: v.title,
              options: options,
              price: finalPrice,
              // Only set when there's an actual discount — _populatePreview
              // renders strikethrough only when original > final.
              original_price: (rawPrice != null && finalPrice < rawPrice) ? rawPrice : null,
            });
          }
        });
        return out.length === optimisticQty ? out : null;
      } catch (_) { return null; }
    })();

    if (window.cartEnhancements && typeof window.cartEnhancements.beginAdd === 'function' && optimisticQty > 0) {
      window.cartEnhancements.beginAdd(optimisticQty, previewItems);
    }

    fetch(window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(built.payload),
    })
      .then(function (response) {
        return response.json().then(function (json) {
          return { ok: response.ok, status: response.status, body: json };
        });
      })
      .then(function (result) {
        if (!result.ok || (result.body && result.body.status)) {
          const msg =
            (result.body && (result.body.description || result.body.message)) ||
            'Sorry, something went wrong adding your bundle. Please try again.';
          if (window.cartEnhancements && typeof window.cartEnhancements.rollbackOptimistic === 'function') {
            window.cartEnhancements.rollbackOptimistic();
          }
          showError(ctx, msg);
          setSubmitEnabled(ctx, true);
          ctx.submitBtn.classList.remove('is-loading');
          return;
        }

        // Success
        ctx.submitBtn.classList.remove('is-loading');
        const cart = built.cart;

        if (cart && typeof cart.renderContents === 'function') {
          cart.renderContents(result.body);
          if (typeof cart.open === 'function') {
            cart.open();
          }
          if (cart.classList && cart.classList.contains('is-empty')) {
            cart.classList.remove('is-empty');
          }
        } else {
          // No drawer/notification — fall back to /cart navigation, matching theme.
          window.location = (window.routes && window.routes.cart_url) || '/cart';
        }
      })
      .catch(function (err) {
        console.error('[bundle-picker] add to cart failed', err);
        if (window.cartEnhancements && typeof window.cartEnhancements.rollbackOptimistic === 'function') {
          window.cartEnhancements.rollbackOptimistic();
        }
        showError(ctx, 'Network error — please check your connection and try again.');
        setSubmitEnabled(ctx, true);
        ctx.submitBtn.classList.remove('is-loading');
      });
  }

  /* ---------- Helpers --------------------------------------------------- */

  function showError(ctx, message) {
    ctx.errorEl.textContent = message;
    ctx.errorEl.removeAttribute('hidden');
  }
  function hideError(ctx) {
    ctx.errorEl.textContent = '';
    ctx.errorEl.setAttribute('hidden', '');
  }

  // Money formatting — cents → string. Uses Shopify.formatMoney if available
  // (theme often exposes window.Shopify with a money_format), otherwise a
  // sensible default of "$X.YY".
  function formatMoney(cents) {
    if (cents == null || isNaN(cents)) return '';
    const SH = window.Shopify;
    if (SH && typeof SH.formatMoney === 'function' && (SH.money_format || (window.theme && window.theme.moneyFormat))) {
      const fmt = SH.money_format || window.theme.moneyFormat;
      try {
        return SH.formatMoney(cents, fmt);
      } catch (_) { /* fall through */ }
    }
    const n = Number(cents) / 100;
    return '$' + n.toFixed(2);
  }

  // Minimal CSS.escape polyfill for older browsers.
  function cssEscape(s) {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(s);
    return String(s).replace(/[^a-zA-Z0-9_-]/g, function (c) {
      return '\\' + c.charCodeAt(0).toString(16) + ' ';
    });
  }

  /* ---------- Boot ------------------------------------------------------ */

  function bootstrap() {
    const wrapper = document.querySelector('.custom-product-bundle[data-bundle="true"]');
    if (!wrapper) return;

    const productJsonEl = document.querySelector('script[data-bundle-product-json]');
    if (!productJsonEl) return;

    let product;
    try { product = JSON.parse(productJsonEl.textContent); }
    catch (e) { console.error('[bundle-picker] product JSON parse error', e); return; }

    // Some products are split across multiple Shopify products (one per
    // colour) and stitched together via the `custom.product_color_variants`
    // metafield. Merge those variants in so the picker shows every colour.
    const linkedEl = document.querySelector('script[data-bundle-linked-variants]');
    if (linkedEl) {
      try {
        const linked = JSON.parse(linkedEl.textContent);
        if (Array.isArray(linked) && linked.length && Array.isArray(product.variants)) {
          const seen = new Set(product.variants.map(function (v) { return v.id; }));
          linked.forEach(function (v) {
            if (v && !seen.has(v.id)) {
              product.variants.push(v);
              seen.add(v.id);
            }
          });
        }
      } catch (e) {
        console.warn('[bundle-picker] linked-variants JSON parse error', e);
      }
    }

    // Ghost cutouts are keyed by the id of the product a colour really belongs
    // to. Linked variants carry their own; the on-page product's own variants
    // already carry `product_id` in `product | json`, but backfill anyway so
    // ghostChain never has to care where a variant came from.
    if (Array.isArray(product.variants)) {
      product.variants.forEach(function (v) {
        if (v && v.product_id == null) v.product_id = product.id;
      });
    }

    let colorSwatches = {};
    const swatchesEl = document.querySelector('script[data-bundle-color-swatches]');
    if (swatchesEl) {
      try { colorSwatches = JSON.parse(swatchesEl.textContent) || {}; }
      catch (e) { console.warn('[bundle-picker] swatch JSON parse error', e); }
    }

    const modal = document.querySelector('.pack-modal[data-bundle-modal]');
    if (!modal) return;
    // Lift the modal out to <body>. It is rendered inside the product info
    // column, which is position: sticky with z-index: 2 on desktop — a
    // stacking context that caps the modal at that level however high its own
    // z-index is, so the sticky site header painted over it. Every modal style
    // is keyed on its own classes and global tokens, so nothing depends on
    // where it sits.
    if (modal.parentElement !== document.body) document.body.appendChild(modal);

    const ctx = {
      wrapper: wrapper,
      product: product,
      colorSwatches: colorSwatches,
      modal: modal,
      modalContent: modal.querySelector('.pack-modal__content'),
      ghostBase: modal.getAttribute('data-ghost-base') || '',
      rowsContainer: modal.querySelector('[data-bundle-rows]'),
      countEls: modal.querySelectorAll('[data-bundle-count]'),
      submitBtn: modal.querySelector('[data-bundle-submit]'),
      submitLabel: modal.querySelector('[data-bundle-submit-label]'),
      packnumEl: modal.querySelector('.packnum'),
      compareEl: modal.querySelector('[data-bundle-compare]'),
      currentEl: modal.querySelector('[data-bundle-current]'),
      currencyCode:
        (modal.querySelector('.pack-modal__footer[data-currency]') || {}).dataset?.currency || '',
      errorEl: modal.querySelector('[data-bundle-error]'),
      ctaBtn: document.getElementById('fake-bundle-btn-add-to-cart'),
      tierLabels: wrapper.querySelectorAll('.bundle-selection label'),
      sizeUi: modal.getAttribute('data-size-ui') || 'pills',
      colorIndex: optionIndex(product, ['color', 'colour']),
      sizeIndex: optionIndex(product, ['size']),
      currentTier: 1,
      tierPaid: {},
      tierPrice: {},
      rows: [],
      lastTrigger: null,
      escHandler: null,
    };

    readTierPaidFromMarkup(ctx);
    syncCurrentTierFromActive(ctx);
    wireTierSelector(ctx);
    wireCta(ctx);
    wireModalDismiss(ctx);

    ctx.submitBtn.addEventListener('click', function () { submitBundle(ctx); });

    // Set initial CTA text from the active tier label so the price/count match
    // the visually-active tier (avoids the old hardcoded "ADD 3 Items..." state).
    const activeLabel = ctx.wrapper.querySelector(
      '.bundle-selection .active-label'
    );
    if (activeLabel) {
      const labelEl = activeLabel.closest('label');
      if (labelEl) updateCtaText(ctx, labelEl);
    }
    updateAtcVisibility(ctx);

    // Prefetch the modal's thumbnail once the page is idle. A multi-pack tier
    // is preselected now, so the modal is the expected next step rather than a
    // detour, and paying for one small image up front buys an instant open.
    // At idle, after load, so it competes with nothing that paints the PDP.
    const warmWhenIdle = function () {
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(function () { warmThumbs(ctx); }, { timeout: 2000 });
      } else {
        setTimeout(function () { warmThumbs(ctx); }, 500);
      }
    };
    if (document.readyState === 'complete') warmWhenIdle();
    else window.addEventListener('load', warmWhenIdle, { once: true });

    // Changing colour on the PDP changes which cutout the modal will open on.
    // Debounced because clicking along a swatch row fires this per swatch and
    // only the colour the shopper settles on is worth fetching.
    let warmTimer = null;
    document.addEventListener('change', function (e) {
      const input = e.target;
      if (!input || input.type !== 'radio' || !input.name) return;
      const name = input.name.toLowerCase();
      if (name !== 'color' && name !== 'colour') return;
      clearTimeout(warmTimer);
      warmTimer = setTimeout(function () { warmThumbs(ctx); }, 300);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
